// services/strategyApi.js - PRODUCTION READY WITH ANALYZER ENDPOINT
export class StrategyApiService {
  constructor(apiBaseUrl, requestManager, dataCache) {
    this.apiBaseUrl = apiBaseUrl;
    this.requestManager = requestManager;
    this.dataCache = dataCache;
  }

  /**
   * Load all bulk strategies with their current database states
   * GET /strategies/api/bulk-strategies/all-current-states/
   */
  async loadBulkStrategies(timeframeFilter = 'all', force = false) {
    const cacheKey = `bulk-strategies-${timeframeFilter}`;
    
    if (!force) {
      const cached = this.dataCache.get(cacheKey);
      if (cached) {
        console.log('[API] Using cached strategies');
        return cached;
      }
    }

    const url = `${this.apiBaseUrl}/strategies/api/bulk-strategies/all-current-states/?lookback_days=190`;
    
    try {
      const response = await this.requestManager.fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      const strategies = Object.entries(data.strategies || {}).map(([id, strategy]) => ({
        bulk_strategy_id: id,
        ...strategy,
      }));

      const result = {
        strategies,
        statusCounts: data.status_counts || null,
      };

      this.dataCache.set(cacheKey, result);
      return result;
      
    } catch (error) {
      console.error('[API] Failed to load bulk strategies:', error);
      throw error;
    }
  }

  /**
   * Load current state of all strategies from the analyzer endpoint
   * GET /strategies/analyze/all/?status=current
   * This endpoint broadcasts state via WebSocket
   */
  async loadAllStrategiesCurrentState(broadcast = true) {
    const cacheKey = 'analyzer_current_state';
    
    if (!broadcast) {
      const cached = this.dataCache.get(cacheKey);
      if (cached) {
        console.log('[API] Using cached analyzer state');
        return cached;
      }
    }

    const url = `${this.apiBaseUrl}/strategies/analyze/all/?status=current&broadcast=${broadcast}`;
    
    try {
      console.log('[API] Loading current state from analyzer...');
      const startTime = performance.now();
      
      const response = await this.requestManager.fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      const apiTime = performance.now() - startTime;
      
      console.log(`[API] Analyzer response in ${apiTime.toFixed(2)}ms`);

      if (data && data.active_strategies) {
        const transformed = this.transformAnalyzerResponse(data);
        
        // Short cache - 10 seconds for real-time data
        this.dataCache.set(cacheKey, transformed, 10000);
        
        return transformed;
      }
      
      return null;
    } catch (error) {
      console.error('[API] Failed to load current state:', error);
      throw error;
    }
  }

  /**
   * Analyze a single strategy
   * GET /strategies/analyze/{bulk_strategy_id}/
   */
  async analyzeStrategy(bulkStrategyId, startDate = null, endDate = null, broadcast = true) {
    let url = `${this.apiBaseUrl}/strategies/analyze/${bulkStrategyId}/?broadcast=${broadcast}`;
    
    if (startDate) {
      url += `&start_date=${startDate}`;
    }
    if (endDate) {
      url += `&end_date=${endDate}`;
    }
    
    try {
      console.log(`[API] Analyzing strategy: ${bulkStrategyId}`);
      const response = await this.requestManager.fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`[API] Failed to analyze ${bulkStrategyId}:`, error);
      throw error;
    }
  }

  /**
   * Load today's trades across all strategies
   * GET /strategies/analyze/all/trades/?start_date=<today_ET>&status=all
   * 
   * Returns { trades, summary, strategy_breakdown }
   * Summary includes: total_trades, winning_trades, losing_trades, win_rate, total_pnl, open_trades
   */
  async loadTodaysTrades(force = false) {
    const cacheKey = 'todays_trades';

    if (!force) {
      const cached = this.dataCache.get(cacheKey);
      if (cached) {
        console.log('[API] Using cached today\'s trades');
        return cached;
      }
    }

    // Build today's date in ET (America/New_York) as ISO start-of-day
    const nowET = new Date(
      new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })
    );
    const startOfDay = new Date(nowET.getFullYear(), nowET.getMonth(), nowET.getDate());
    const startDateISO = startOfDay.toISOString();

    const url = `${this.apiBaseUrl}/strategies/analyze/all/trades/?start_date=${startDateISO}&status=all&limit=200`;

    try {
      console.log('[API] Loading today\'s trades...');
      const startTime = performance.now();

      const response = await this.requestManager.fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const apiTime = performance.now() - startTime;

      console.log(`[API] Today's trades loaded in ${apiTime.toFixed(2)}ms (${data.trades?.length || 0} trades)`);

      const result = {
        trades: data.trades || [],
        summary: data.summary || {},
        strategy_breakdown: data.strategy_breakdown || [],
      };

      // Cache for 30 seconds - trades update with entries/exits
      this.dataCache.set(cacheKey, result, 30000);

      return result;
    } catch (error) {
      console.error('[API] Failed to load today\'s trades:', error);
      throw error;
    }
  }

  /**
   * Transform analyzer endpoint response to component format
   */
  transformAnalyzerResponse(response) {
    const { summary, active_strategies, timestamp } = response;
    
    const strategies = (active_strategies || []).map(strategy => {
      const completionPct = strategy.completion_percentage || 0;
      
      // Count total sub-strategies
      const requiredCount = Object.keys(strategy.required_signals || {}).length;
      const confirmationCount = Object.keys(strategy.confirmations || {}).length;
      const totalSubStrategies = requiredCount + confirmationCount;
      
      // Calculate completed count from percentage
      const completedCount = Math.round((completionPct / 100) * totalSubStrategies);
      
      // Build sub_strategies array for card display
      const subStrategies = [];
      
      // Add required signals as sub-strategies
      Object.entries(strategy.required_signals || {}).forEach(([id, data]) => {
        const isCompleted = data.early_signal?.time !== null && data.confirmation_signal?.time !== null;
        subStrategies.push({
          strategy_id: id,
          type: 'required',
          condition_type: data.type,
          early_signal: data.early_signal,
          confirmation_signal: data.confirmation_signal,
          min_minutes: data.min_minutes,
          max_minutes: data.max_minutes,
          completed: isCompleted,
        });
      });
      
      // Add confirmations as sub-strategies
      const pendingList = strategy.pending_confirmations || [];
      Object.entries(strategy.confirmations || {}).forEach(([id, data]) => {
        const isPending = pendingList.includes(id);
        subStrategies.push({
          strategy_id: id,
          type: 'confirmation',
          condition_type: data.type,
          early_signal: data.early_signal,
          confirmation_signal: data.confirmation_signal,
          min_minutes: data.min_minutes,
          max_minutes: data.max_minutes,
          completed: !isPending,
        });
      });

      return {
        // Core identifiers
        bulk_strategy_id: strategy.strategy_id,
        name: strategy.strategy_id,
        
        // Strategy config
        symbol: strategy.symbol || 'NQ',
        timeframe: strategy.timeframe || '1m',
        direction: strategy.direction || 'LONG',
        trading_hour: strategy.trading_hour || 'ETH',
        
        // CRITICAL: Preserve time_restriction for filtering
        time_restriction: strategy.time_restriction || null,
        
        // Status flags
        is_active: true,
        has_data: true,
        
        // Current state
        current_state: {
          status: strategy.status,
          in_position: strategy.status === 'open',
          entry_time: strategy.entry_time || null,
          entry_price: strategy.entry_price || null,
          trigger_signal: strategy.trigger_signal,
          required_signals: strategy.required_signals,
          confirmations: strategy.confirmations,
          pending_confirmations: pendingList,
          all_sub_strategies_state: this.buildSubStrategiesState(strategy),
        },
        
        // Progress
        progress: {
          completed: completedCount,
          total: totalSubStrategies,
          percentage: completionPct,
        },
        
        // Sub-strategies for card display
        sub_strategies: subStrategies,
        
        // Metadata
        last_updated: timestamp,
      };
    });

    return {
      strategies,
      summary: {
        total: summary?.total_strategies || 0,
        pending: summary?.pending || 0,
        open: summary?.open || 0,
        idle: summary?.idle || 0,
      },
      timestamp,
      statusCounts: {
        has_data: strategies.length,
        table_empty: 0,
        table_missing: 0,
      },
    };
  }

  /**
   * Build sub-strategies state map for component compatibility
   */
  buildSubStrategiesState(strategy) {
    const state = {};
    
    // Required signals - completed if both times are present
    Object.entries(strategy.required_signals || {}).forEach(([id, data]) => {
      const isCompleted = data.early_signal?.time !== null && data.confirmation_signal?.time !== null;
      state[id] = {
        completed: isCompleted,
        condition_type: data.type,
        early_signal: data.early_signal,
        confirmation_signal: data.confirmation_signal,
        last_update: data.confirmation_signal?.time || data.early_signal?.time,
      };
    });
    
    // Confirmations - pending if in pending_confirmations list
    const pendingList = strategy.pending_confirmations || [];
    Object.entries(strategy.confirmations || {}).forEach(([id, data]) => {
      state[id] = {
        completed: !pendingList.includes(id),
        condition_type: data.type,
        early_signal: data.early_signal,
        confirmation_signal: data.confirmation_signal,
        last_update: data.confirmation_signal?.time || data.early_signal?.time,
      };
    });
    
    return state;
  }

  /**
   * Load all strategies with computed states (legacy endpoint)
   * GET /strategies/api/bulk-strategies/all-current-states-computed/
   */
  async loadAllStrategiesComputed(lookbackDays = 190, force = false) {
    const cacheKey = `computed-states-${lookbackDays}`;
    
    if (!force) {
      const cached = this.dataCache.get(cacheKey);
      if (cached) {
        console.log('[API] Using cached computed states');
        return cached;
      }
    }

    const url = `${this.apiBaseUrl}/strategies/api/bulk-strategies/all-current-states-computed/?lookback_days=${lookbackDays}`;
    
    try {
      console.log('[API] Computing states for all strategies...');
      const response = await this.requestManager.fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      const strategies = (data.strategies || []).map(strategy => {
        const current_state = strategy.current_state || {};
        const progress = current_state.progress_towards_next_entry || { completed: 0, total: 0, percentage: 0 };
        
        return {
          bulk_strategy_id: strategy.bulk_strategy_id,
          success: strategy.success,
          current_state: current_state,
          total_complete_trades: strategy.total_complete_trades,
          progress: progress,
          last_exit_time: current_state.last_exit_time,
          tracking_status: current_state.tracking_status,
          has_data: true,
        };
      });

      const result = {
        strategies,
        total_strategies: data.total_strategies,
        successful: data.successful,
        failed: data.failed,
        websocket_broadcast: data.websocket_broadcast,
      };

      this.dataCache.set(cacheKey, result, 30000);
      console.log(`[API] âœ… Computed ${data.successful}/${data.total_strategies} strategies`);
      return result;
      
    } catch (error) {
      console.error('[API] Failed to load computed states:', error);
      throw error;
    }
  }

  /**
   * Load validated broadcasts for a strategy
   */
  async loadStrategyBroadcasts(bulkStrategyId, force = false) {
    const cacheKey = `broadcasts-${bulkStrategyId}`;
    
    if (!force) {
      const cached = this.dataCache.get(cacheKey);
      if (cached) return cached;
    }

    const url = `${this.apiBaseUrl}/strategies/api/bulk-strategies/${bulkStrategyId}/validated-broadcasts/`;
    
    try {
      const response = await this.requestManager.fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      
      const result = {
        broadcasts: data.broadcasts || [],
        is_historical: data.is_historical || false,
        data_age: `${data.lookback_days || 24} hours`,
      };
      
      this.dataCache.set(cacheKey, result);
      return result;
      
    } catch (error) {
      console.error(`[API] Failed to load broadcasts for ${bulkStrategyId}:`, error);
      throw error;
    }
  }

  /**
   * Load trades for a strategy
   */
  async loadStrategyTrades(bulkStrategyId) {
    const url = `${this.apiBaseUrl}/strategies/api/bulk-strategies/${bulkStrategyId}/trades/`;
    
    try {
      const response = await this.requestManager.fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      return await response.json();
    } catch (error) {
      console.error(`[API] Failed to load trades for ${bulkStrategyId}:`, error);
      throw error;
    }
  }

  /**
   * Check active positions
   */
  async checkActivePositions() {
    const url = `${this.apiBaseUrl}/strategies/api/bulk-strategies/active-positions/`;
    
    try {
      const response = await this.requestManager.fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      return data.active_strategies || {};
    } catch (error) {
      console.error('[API] Failed to check active positions:', error);
      return {};
    }
  }

  /**
   * Sync a single strategy
   */
  async syncStrategy(bulkStrategyId) {
    const url = `${this.apiBaseUrl}/strategies/api/bulk-strategies/${bulkStrategyId}/sync/`;
    
    try {
      const response = await this.requestManager.fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error(`[API] Failed to sync ${bulkStrategyId}:`, error);
      return { success: false, error: error.message };
    }
  }
}
